import React, { useCallback, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useDismiss } from './useDismiss';
import { useMenuKeys } from './useMenuKeys';

interface UserMenuProps {
  /** Who is signed in: the email when there is one, otherwise the username. */
  label: string;
  /** Called when the person picks Sign out. The shell asks for confirmation before it signs out. */
  onRequestSignOut: () => void;
  /** Read-only lines shown under the name, for what the bar has no room for (see the `c3-show-le*` classes). */
  info?: React.ReactNode;
  /** Menu entries the bar has no room for at narrow widths (see the `c3-show-le*` classes). */
  children?: React.ReactNode;
}

function initialsOf(label: string): string {
  const name = label.split('@')[0];
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return letters.toUpperCase() || '?';
}

const UserMenu: React.FC<UserMenuProps> = ({ label, onRequestSignOut, info, children }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close, triggerRef);
  const keys = useMenuKeys(rootRef, open, setOpen);

  return (
    <div className="c3-nav-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="c3-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title={label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={keys.onTriggerKeyDown}
      >
        {initialsOf(label)}
      </button>
      {open && (
        <div
          className="c3-nav-menu-panel"
          onKeyDown={keys.onMenuKeyDown}
          onClick={(e) => {
            // Picking any entry, including the ones the bar passes in, closes the menu.
            // The menu goes away with its entries, so the focus returns to the button first: a dialog opened
            // from an entry gives the focus back to what had it when it opened.
            if ((e.target as HTMLElement).closest('[role="menuitem"], [role="menuitemradio"]')) {
              triggerRef.current?.focus();
              setOpen(false);
            }
          }}
        >
          <div className="c3-nav-menu-heading">Signed in as</div>
          <div className="c3-user-email">{label}</div>
          {info}
          <div role="menu" aria-label="Account">
            {children}
            <div className="c3-nav-menu-sep" role="separator" />
            <button
              type="button"
              className="c3-nav-menu-item"
              role="menuitem"
              onClick={() => {
                // The dialog gives focus back to the element focused when it opened.
                triggerRef.current?.focus();
                setOpen(false);
                onRequestSignOut();
              }}
            >
              <span className="inline-flex items-center gap-2">
                <LogOut size={16} strokeWidth={1.5} aria-hidden />
                Sign out
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
